<script setup>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import Helmet from '@/components/Helmet.vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

function goToContact() {
  if (route.path === '/') {
    // Si ya estás en la página principal, simplemente agregamos el hash
    router.replace({ hash: '#contact' })
  } else {
    // Si estás en otra ruta, redirige a la home con el hash
    router.push({ path: '/', hash: '#contact' })
  }
}
</script>

<template>
  <Helmet title="Cómo ayudamos a negocios locales y empresas a tener presencia online desde cero | Resi Corporation" />
  <Header />

  <section class="container">
    <h2 class="title">Cómo ayudamos a negocios locales a tener presencia online desde cero</h2>

    <p class="summary">
      Paso a paso de nuestro método para que comercios barriales y pymes conquisten internet sin dolores de cabeza.
    </p>

    <div class="meta">
      <span class="author">Por Máximo Felipe Resi</span>
      <span class="date">9 de junio de 2025</span>
    </div>

    <div class="image-wrapper">
      <img src="../../public/img/presencia-online.png" alt="Negocio local con presencia online" title="Presencia digital para pymes" loading="lazy" />
    </div>

    <article class="content">
      <h2>1. Diagnóstico del negocio</h2>
      <p>
        Analizamos tu propuesta de valor, público objetivo, ubicación y nivel de digitalización actual. Esto nos permite diseñar una estrategia personalizada que maximice tu alcance local y se alinee con los hábitos de consumo online de tus clientes potenciales.
      </p>

      <h2>2. Branding y mensaje</h2>
      <p>
        Desarrollamos la identidad visual de tu marca desde cero o la renovamos si es necesario. Definimos logotipo, colores, tipografías y el tono de voz que vas a utilizar tanto en redes sociales como en tu página web, garantizando una presencia coherente y profesional.
      </p>

      <h2>3. Desarrollo del sitio web</h2>
      <p>
        Diseñamos una <strong>página web moderna, responsive y orientada a resultados</strong>. Tu sitio será compatible con celulares y computadoras, con tiempos de carga rápidos, arquitectura clara y contenido optimizado para SEO local. Implementamos prácticas recomendadas como etiquetas meta, alt en imágenes y estructura H1/H2 efectiva.
      </p>

      <h2>4. Integraciones clave</h2>
      <p>
        Incorporamos funciones prácticas que hacen la diferencia a la hora de convertir visitas en ventas:
      </p>
      <ul>
        <li><strong>Google Maps:</strong> Para que los clientes te encuentren fácilmente y reciban indicaciones.</li>
        <li><strong>Formularios:</strong> De contacto, reservas o pedidos para facilitar la interacción.</li>
        <li><strong>WhatsApp Business:</strong> Atención directa con solo un clic desde cualquier dispositivo.</li>
        <li><strong>Email marketing:</strong> Para fidelizar a tus clientes y notificar promociones o novedades.</li>
      </ul>

      <h2>5. Lanzamiento y promoción digital</h2>
      <p>
        Nos encargamos de que tu negocio esté presente en todas las plataformas clave:
      </p>
      <ul>
        <li>Alta en <strong>Google Business Profile</strong> (ex Google Mi Negocio) para aparecer en búsquedas y mapas.</li>
        <li>Enlace con redes sociales (Facebook, Instagram, TikTok) con diseño uniforme.</li>
        <li>Campañas segmentadas en Google Ads o Meta Ads para captar clientes locales.</li>
      </ul>

      <h2>Historias de éxito reales</h2>
      <p>
        <strong>Distribuidora de alimentos:</strong> aumentó un 150% sus pedidos gracias al nuevo sitio web con fotos atractivas, SEO local y promoción en redes.<br>
        <strong>Negocio de Tecnologia:</strong> recibió más de 80 clientes nuevos en tres meses tras el lanzamiento de su sitio y campañas de marketing digital geolocalizadas.
      </p>

      <h2>Beneficios de tener presencia online profesional</h2>
      <p>
        Una presencia digital no solo mejora tu visibilidad, sino que te posiciona como opción confiable frente a tus competidores. Al tener una web optimizada y conectada a tus canales de atención, podés generar confianza, destacar promociones, contar tu historia y captar clientes nuevos incluso mientras dormís.
      </p>

      <h2>¿Querés ser nuestro próximo caso de éxito?</h2>
      <p class="engagement">
        Desde Resi Corporation ayudamos a pequeños negocios y emprendedores a profesionalizar su imagen digital sin depender de plataformas externas. Damos acompañamiento real, resultados visibles y atención personalizada.
      </p>

      <!-- CTA -->
      <div class="cta-wrapper">
        <a @click.prevent="goToContact" href="#" class="cta-link">Agendá una llamada gratuita</a>
      </div>

      <!-- FAQ Section -->
      <h2>Preguntas frecuentes sobre presencia online</h2>
      <ul>
        <li><strong>¿Necesito tener redes sociales para tener un sitio web?</strong><br>No es obligatorio, pero recomendamos integrarlas para aprovechar el tráfico cruzado y mejorar tu alcance.</li>
        <li><strong>¿Cuánto tarda en estar lista mi web?</strong><br>Entre 1 y 4 semanas, dependiendo del contenido, fotos y complejidad del proyecto.</li>
        <li><strong>¿Puedo actualizar mi sitio después?</strong><br>¡Sí! Ofrecemos mantenimiento mensual y capacitaciones para que tengas el control total.</li>
        <li><strong>¿Cuánto cuesta crear una página web profesional?</strong><br>Tenemos planes accesibles desde U$D 200 con todo incluido (desarrollo, carga inicial, dominio y 1 mes de mantenimiento gratis).</li>
      </ul>
    </article>
  </section>

  <Footer />
</template>

<style scoped>
.container {
  margin-top: 120px !important;
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
}

.title {
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.summary {
  font-size: 1.1rem;
  color: var(--text-color);
  margin-bottom: 20px;
  line-height: 1.6;
}

.meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: var(--text-color);
  margin-bottom: 25px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.image-wrapper {
  margin-bottom: 30px;
}

.image-wrapper img {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.content {
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-color);
}

.content h2 {
  font-size: 1.6rem;
  margin-top: 30px;
  margin-bottom: 15px;
}

.content p,
.content ul {
  margin-bottom: 20px;
}

.content ul {
  padding-left: 1.5rem;
}

.content ul li {
  margin-bottom: 0.7rem;
  line-height: 1.5;
}

.engagement {
  color: var(--title-color);
  font-weight: 600;
}

.cta-wrapper {
  text-align: center;
  margin-top: 30px;
}

.cta-link {
  display: inline-block;
  margin-top: 1rem;
  background: var(--first-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.3s ease;
}

.cta-link:hover {
  background: red;
}
</style>
